import React, { useState } from 'react';
import styled from 'styled-components';

import { FileModel, FolderModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { permissionChecks } from '../../../../utils/permission-check';
import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { AppContext } from '../../../state/app-state';
import { ProjectContext } from '../../../state/project-state/project-state';
import { lastFileVersionHasDxfOrGlb } from '../../../utils/file';
import {
  IntegrationListStatus,
  IntegrationsContext,
} from '../../integrations/state/integrations-state';
import { Table } from '../../table/table';
import { MoveFileFolderDialog } from '../dialogs/move-file-folder-dialog/move-file-folder-dialog';
import { FolderListViewContext } from '../state/folder-list-view-state';
import { TreeContext } from '../state/folder-tree-state';
import { FileRow } from './file-row';
import { FolderRow } from './folder-row';
import { HeaderRow } from './header-row';

type CheckableFolderModel = FolderModel & { checked: boolean; __src: FolderModel };
type CheckableFileModel = FileModel & { checked: boolean; __src: FileModel };

type TableState = {
  headerChecked?: boolean;
  folders: CheckableFolderModel[];
  files: CheckableFileModel[];
};
type TableAction =
  | { type: 'INIT'; folders: FolderModel[]; files: FileModel[] }
  | { type: 'TOGGLE_ALL'; checked: boolean }
  | { type: 'TOGGLE_FOLDER'; id: string; checked: boolean }
  | { type: 'TOGGLE_FILE'; id: string; checked: boolean };

interface Props {
  files: FileModel[];
  folders: FolderModel[];
}

const FolderListViewTable = (props: Props) => {
  const { isAdminForCompany } = React.useContext(AppContext);
  const { project } = React.useContext(ProjectContext);
  const {
    setCheckedItems,
    setSelectedFile,
    toggleSelectedFile,
    checkedFiles,
    checkedFolders,
    selectedFile,
    foldersCheckable,
    filesCheckable,
    maxFileSelection,
  } = React.useContext(FolderListViewContext);
  const { integrationStatus, integrations } = React.useContext(IntegrationsContext);
  const { selectAndOpenFolder, isCurrentFolderWithinActiveConnectFolder } =
    React.useContext(TreeContext);

  const [table, dispatchTable] = React.useReducer(tableReducer, {
    headerChecked: false,
    folders: [],
    files: [],
  });

  const moveFileFolderDialog = useDialog();
  const [targetFolder, setTargetFolder] = useState<{ id: string; name: string }>();
  React.useEffect(() => {
    setCheckedItems(
      table.folders.filter((x) => x.checked),
      table.files.filter((x) => x.checked),
    );

    // If any file is previously selected, update file info panel
    // by selecting this file again. For instance when uploadinging
    // a file that already exists, update file panel to display new
    // version.
    if (selectedFile) {
      const currentSelectedFile = table.files.find((x) => x.id === selectedFile.id);
      if (currentSelectedFile) {
        setSelectedFile(currentSelectedFile);
      }
    }
  }, [table.folders, table.files, selectedFile, setSelectedFile, setCheckedItems]);

  React.useEffect(() => {
    dispatchTable({
      type: 'INIT',
      folders: props.folders,
      files: props.files,
    });
  }, [project, props.files, props.folders]);

  function tableReducer(state: TableState, action: TableAction): TableState {
    switch (action.type) {
      case 'INIT': {
        // When initializing new list of files, check those files that were previously checked.
        // For instance, if 8 of 10 selected files could be deleted, when closing the delete dialog
        // the remaining 2 selected should be selected after list refresh.
        // Use Maps for O(n) lookup and preserve existing row references when the underlying model
        // hasn't changed (__src check), so React.memo on FileRow/FolderRow can bail out.
        const existingFoldersMap = new Map(state.folders.map((f) => [f.id, f]));
        const existingFilesMap = new Map(state.files.map((f) => [f.id, f]));

        const newState: TableState = {
          headerChecked: false,
          folders: action.folders.map((x) => {
            const existing = existingFoldersMap.get(x.id);
            const checked = isDefined(
              checkedFolders.find((prevSelectedFolder) => prevSelectedFolder.id === x.id),
            );
            if (existing && existing.__src === x && existing.checked === checked) {
              return existing;
            }
            return { ...x, checked, __src: x };
          }),
          files: action.files.map((x) => {
            const existing = existingFilesMap.get(x.id);
            const checked = isDefined(
              checkedFiles.find((prevSelectedFile) => prevSelectedFile.id === x.id),
            );
            if (existing && existing.__src === x && existing.checked === checked) {
              return existing;
            }
            return { ...x, checked, __src: x };
          }),
        };

        newState.headerChecked = calcHeaderCheckBoxState(newState);

        return newState;
      }

      case 'TOGGLE_ALL': {
        // Returns a new object for model with checked property changed if current model check
        // state is not equal to action check state. Otherwise same model object is returned.
        const toggle = <T extends CheckableFileModel | CheckableFolderModel>(model: T): T =>
          model.checked === action.checked ? model : { ...model, checked: action.checked };

        const newState = {
          ...state,
          folders: foldersCheckable ? state.folders.map(toggle) : state.folders,
          files: filesCheckable ? state.files.map(toggle) : state.files,
        };

        newState.headerChecked = calcHeaderCheckBoxState(newState);
        return newState;
      }

      case 'TOGGLE_FOLDER': {
        const newState = {
          ...state,
          folders: state.folders.map((x) =>
            x.id === action.id
              ? {
                  ...x,
                  checked: action.checked,
                }
              : x,
          ),
        };

        newState.headerChecked = calcHeaderCheckBoxState(newState);
        return newState;
      }

      case 'TOGGLE_FILE': {
        const newState = {
          ...state,
          files: state.files.map((x) =>
            x.id === action.id
              ? {
                  ...x,
                  checked: action.checked,
                }
              : x,
          ),
        };

        newState.headerChecked = calcHeaderCheckBoxState(newState);
        return newState;
      }
    }
  }

  function calcHeaderCheckBoxState(state: TableState) {
    const noneChecked =
      state.folders.every((x) => !x.checked) && state.files.every((x) => !x.checked);
    const allChecked =
      (foldersCheckable ? state.folders.every((x) => x.checked) : true) &&
      (filesCheckable ? state.files.every((x) => x.checked) : true);

    return noneChecked || allChecked ? allChecked : undefined;
  }
  const onHeaderSelectionChanged = React.useCallback((checked: boolean) => {
    dispatchTable({
      type: 'TOGGLE_ALL',
      checked,
    });
  }, []);

  const onFolderClicked = React.useCallback(
    (folder: FolderModel) => {
      selectAndOpenFolder(folder.id);
    },
    [selectAndOpenFolder],
  );

  const onFolderSelectionChanged = React.useCallback((checked: boolean, folder: FolderModel) => {
    dispatchTable({
      type: 'TOGGLE_FOLDER',
      id: folder.id,
      checked,
    });
  }, []);

  const onFileClicked = React.useCallback(
    (file: FileModel) => {
      toggleSelectedFile(file);
    },
    [toggleSelectedFile],
  );

  const onFileSelectionChanged = React.useCallback((checked: boolean, file: FileModel) => {
    dispatchTable({
      type: 'TOGGLE_FILE',
      id: file.id,
      checked,
    });
  }, []);

  const isSyncActive = () =>
    integrationStatus === IntegrationListStatus.Success && integrations.length > 0;

  const displayCanBeAddedToSkyViewColumn = table.files.some(lastFileVersionHasDxfOrGlb);
  const displayFolderTags =
    permissionChecks.hasAccessToFeature('feature:file_manager:tags', [
      isAdminForCompany(project.companyId),
    ]) || false;
  const displaySharedColumn =
    (permissionChecks.hasAccessToFeature('feature:file_manager:share_folder', [
      isAdminForCompany(project.companyId),
    ]) &&
      table.folders.some((x) => x.sharedFolder)) ||
    false;
  const displayTagColumn =
    (permissionChecks.hasAccessToFeature('feature:file_manager:tags', [
      isAdminForCompany(project.companyId),
    ]) &&
      table.folders.some((x) => x.tags?.length ?? 0 > 0)) ||
    false;

  // Because dongles does not count towards integrations,
  // it is not possible to display active connect folder if only dongles are connected to the project.
  // TODO: Return integration check when dongles are counted towards existing integrations
  // https://skymapinnovations.atlassian.net/browse/SK-4274
  const displaySkyMapConnectColumn =
    (isSyncActive() || true) &&
    permissionChecks.hasAccessToFeature('feature:file_manager:connect_folder', [
      isAdminForCompany(project.companyId),
    ]) &&
    table.folders.some((x) => x.skyMapConnectFolder);

  const displayCheckboxCell =
    (foldersCheckable && table.folders.length > 0) || (filesCheckable && table.files.length > 0);

  const displayFileCheckbox = (fileId: string) => {
    if (!isDefined(maxFileSelection)) {
      return filesCheckable;
    }

    const canSelectMoreFiles = checkedFiles.length < maxFileSelection;
    const isThisFileSelected = checkedFiles.some((selectedFile) => selectedFile.id === fileId);

    return filesCheckable && (isThisFileSelected || canSelectMoreFiles);
  };

  return (
    <Component>
      <Table.Head>
        <HeaderRow
          checked={table.headerChecked}
          checkVolatileName={isCurrentFolderWithinActiveConnectFolder}
          disableCheckbox={maxFileSelection === 1}
          displayCanBeAddedToSkyViewColumn={displayCanBeAddedToSkyViewColumn}
          displayCheckbox={displayCheckboxCell}
          displaySharedColumn={displaySharedColumn}
          displaySkyMapConnectColumn={displaySkyMapConnectColumn}
          displayTagColumn={displayTagColumn}
          onSelectionChanged={onHeaderSelectionChanged}
        />
      </Table.Head>
      <Table.Body>
        {table.folders.map((folder) => (
          <FolderRow
            checked={folder.checked}
            checkVolatileName={isCurrentFolderWithinActiveConnectFolder}
            displayCanBeAddedToSkyViewColumn={displayCanBeAddedToSkyViewColumn}
            displayCheckbox={foldersCheckable}
            displayCheckboxCell={displayCheckboxCell}
            displayFolderTags={displayFolderTags}
            displaySharedColumn={displaySharedColumn}
            displaySkyMapConnectColumn={displaySkyMapConnectColumn}
            displayTagColumn={displayTagColumn}
            folder={folder}
            key={folder.id}
            moveFileDialog={moveFileFolderDialog}
            onClick={onFolderClicked}
            onSelectionChanged={onFolderSelectionChanged}
            onTargetFolderChanged={setTargetFolder}
          />
        ))}

        {table.files.map((file) => (
          <FileRow
            checked={file.checked}
            checkVolatileName={isCurrentFolderWithinActiveConnectFolder}
            displayCanBeAddedToSkyViewColumn={displayCanBeAddedToSkyViewColumn}
            displayCheckbox={displayFileCheckbox(file.id)}
            displayCheckboxCell={displayCheckboxCell}
            displaySharedColumn={displaySharedColumn}
            displaySkyMapConnectColumn={displaySkyMapConnectColumn}
            displayTagColumn={displayTagColumn}
            file={file}
            key={file.id}
            moveFileDialog={moveFileFolderDialog}
            selected={selectedFile?.id === file.id}
            onClick={onFileClicked}
            onSelectionChanged={onFileSelectionChanged}
            onTargetFolderChanged={setTargetFolder}
          />
        ))}
      </Table.Body>

      {targetFolder &&
        moveFileFolderDialog.render(
          <MoveFileFolderDialog
            targetFolder={targetFolder}
            onClose={() => {
              moveFileFolderDialog.hide();
            }}
          />,
        )}
    </Component>
  );
};

const Component = styled(Table)`
  tr td:not(:empty) {
    border: 1px solid #000 !important;
    display: none;
  }
`;

export { FolderListViewTable };
